import { omitUndefinedDeep } from '$lib/utils/omitUndefined';

export const SubscriptionType: Record<string, string> = {
    MOLLIE: 'MOLLIE',
    SCHOOL: 'SCHOOL',
    SPECIAL: 'SPECIAL',
    ORGANIZATION: 'ORGANIZATION'
};

type ValidatorFn = (t: (...args: any[]) => any) => string;
interface ValidatorResult {
    isExpired: boolean;
    isCanceled: boolean;
    isCancellable: boolean;
    isDeletable: boolean;
    renderSubscriptionMessage: ValidatorFn;
    renderSubscriptionName: ValidatorFn;
    renderSubscriptionState?: ValidatorFn;
}

/**
 * Coerce Firestore Timestamps, Dates, unix-seconds objects, ISO strings,
 * and epoch millis into a Date. Unparseable values return null.
 */
function toJsDate(value: unknown): Date | null {
    if (value == null) {
        return null;
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'object') {
        const maybeTimestamp = value as {
            toDate?: unknown;
            seconds?: unknown;
            _seconds?: unknown;
            nanoseconds?: unknown;
            _nanoseconds?: unknown;
        };

        if (typeof maybeTimestamp.toDate === 'function') {
            try {
                return toJsDate((maybeTimestamp.toDate as () => unknown)());
            } catch {
                return null;
            }
        }

        const seconds = maybeTimestamp.seconds ?? maybeTimestamp._seconds;
        if (typeof seconds === 'number' && Number.isFinite(seconds)) {
            const nanos = maybeTimestamp.nanoseconds ?? maybeTimestamp._nanoseconds;
            const millis = seconds * 1000 + (typeof nanos === 'number' ? nanos / 1e6 : 0);
            const date = new Date(millis);
            return Number.isNaN(date.getTime()) ? null : date;
        }

        return null;
    }

    if (typeof value === 'string' || typeof value === 'number') {
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    return null;
}

function formatEndDate(value: unknown): string {
    return toJsDate(value)?.toLocaleDateString() ?? "Unknown";
}

// SEPA payments arrive days late while Mollie keeps the subscription active, so the end date is not enforced right away
const MOLLIE_GRACE_PERIOD_MS = 3 * 24 * 60 * 60 * 1000;

const validators: Record<string, (subscription: any) => ValidatorResult> = {
    MOLLIE: (subscription: any) => {
        const isCanceled = subscription.metadata.canceled || subscription.metadata.status === "canceled" || subscription.metadata.status === "deleted";
        // a deliberately canceled subscription gets no grace period
        const gracePeriod = isCanceled ? 0 : MOLLIE_GRACE_PERIOD_MS;
        let isExpired = false;
        const endDate = toJsDate(subscription.endDate);
        if (endDate && endDate.getTime() + gracePeriod < Date.now()) {
            isExpired = true;
        } else if (!subscription.endDate && (subscription.metadata.status === "canceled" || subscription.metadata.status === "deleted")) {
            isExpired = true;
        } else {
            isExpired = subscription.metadata.status === "pending";
        }

        return {
            isExpired,
            isCanceled,
            isCancellable: !isExpired && !isCanceled,
            isDeletable: (isExpired || isCanceled) && subscription.metadata.status !== "deleted",
            renderSubscriptionMessage: (t: (...args: any[]) => any) => {
                if (isExpired) {
                    return t("subscription.mollie.message.expired");
                } else {
                    return t("subscription.mollie.message.active", { endDate: formatEndDate(subscription.endDate) });
                }
            },
            renderSubscriptionName: (t: (...args: any[]) => any) => {
                return t("subscription.mollie.name")
            },
            renderSubscriptionState: (t: (...args: any[]) => any) => {
                if (subscription.metadata.status === "deleted") {
                    return t("subscription.deleted");
                } else if (isCanceled) {
                    return t("subscription.mollie.canceled");
                } else if (isExpired) {
                    return t("subscription.expired");
                } else {
                    return t("subscription.active");
                }
            }
        }
    },
    SCHOOL: (subscription: any) => {
        let isExpired = false;
        if (typeof subscription.endDate !== "undefined") {
            const endDate = toJsDate(subscription.endDate);
            isExpired = endDate != null && endDate < new Date();
        }

        const isCanceled = subscription.metadata.canceled || subscription.metadata.status === "deleted";
        return {
            isExpired,
            isCancellable: false,
            isCanceled,
            isDeletable: (isExpired || isCanceled) && subscription.metadata.status !== "deleted",
            renderSubscriptionMessage: (t: (...args: any[]) => any) => {
                if (isExpired) {
                    return t("subscription.school.message.expired");
                } else {
                    return t("subscription.school.message.active", { endDate: formatEndDate(subscription.endDate) });
                }
            },
            renderSubscriptionName: (t: (...args: any[]) => any) => {
                if (subscription?.metadata?.schoolName) {
                    return `${t("subscription.school.name")} - ${subscription.metadata.schoolName}`;
                }
                return `${t("subscription.school.name")}`
            }
        }
    },
    SPECIAL: (subscription: any) => {
        const isCanceled = subscription.metadata.canceled || subscription.metadata.status === "deleted";
        let isExpired = false;
        const endDate = toJsDate(subscription.endDate);
        if (endDate && endDate < new Date()) {
            isExpired = true;
        } else if (!subscription.endDate && !subscription.metadata.active) {
            isExpired = true;
        } else {
            isExpired = !subscription.metadata.active;
        }

        return {
            isExpired: isExpired,
            isCancellable: false,
            isCanceled,
            isDeletable: (isExpired || isCanceled) && subscription.metadata.status !== "deleted",
            renderSubscriptionMessage: (t: (...args: any[]) => any) => {
                if (!subscription.metadata.active || isExpired) {
                    return t("subscription.special.message.expired");
                } else {
                    if (subscription.endDate) {
                        return t("subscription.special.message.active.until", { endDate: formatEndDate(subscription.endDate) });
                    }
                    return t("subscription.special.message.active");
                }
            },
            renderSubscriptionName: (t: (...args: any[]) => any) => {
                return t("subscription.special.name")
            }
        }
    },
    ORGANIZATION: (subscription: any) => {
        const isCanceled = subscription.metadata?.status === "revoked" || subscription.metadata?.status === "canceled" || subscription.metadata?.status === "deleted";
        return {
            isExpired: isCanceled,
            isCanceled,
            isCancellable: false,
            isDeletable: isCanceled,
            renderSubscriptionMessage: (t: (...args: any[]) => any) => {
                if (isCanceled) {
                    return t("subscription.organization.message.revoked");
                }
                return t("subscription.organization.message.active", { orgName: subscription.metadata?.orgName ?? "Unknown" });
            },
            renderSubscriptionName: (t: (...args: any[]) => any) => {
                return t("subscription.organization.name", { orgName: subscription.metadata?.orgName ?? "Unknown" });
            }
        }
    }
};

export class Subscription {
    metadata: Record<string, any> = {};
    startDate: any = null;
    endDate: any = null;
    _type: string = 'MOLLIE';

    constructor(startDate?: any, endDate?: any, metadata?: any) {
        this.startDate = startDate ?? null;
        this.endDate = endDate ?? null;
        this.metadata = metadata ?? {};
    }

    isExpired(): boolean {
        return validators[this.type](this).isExpired;
    }

    isCancellable(): boolean {
        return validators[this.type](this).isCancellable;
    }

    isDeletable(): boolean {
        return validators[this.type](this).isDeletable;
    }

    isCanceled(): boolean {
        return validators[this.type](this).isCanceled;
    }

    renderSubscriptionName(t: (...args: any[]) => any): string {
        return validators[this.type](this).renderSubscriptionName(t);
    }

    renderSubscriptionState(t: (...args: any[]) => any): string {
        const result = validators[this.type](this);
        if (result.renderSubscriptionState) {
            return result.renderSubscriptionState(t);
        }

        if (result.isExpired) {
            return t("subscription.expired");
        }
        return t("subscription.active");
    }

    renderSubscriptionMessage(t: (...args: any[]) => any): string {
        return validators[this.type](this).renderSubscriptionMessage(t);
    }

    static fromJson(json: any): Subscription {
        const subscription = new Subscription();
        subscription.startDate = json.startDate;
        subscription.endDate = json.endDate;
        subscription.metadata = json.metadata ?? {};
        subscription.type = json.type;
        return subscription;
    }

    toJson(): any {
        const json: Record<string, any> = {
            metadata: omitUndefinedDeep(this.metadata ?? {}),
            type: this.type
        };
        if (this.startDate !== undefined) {
            json.startDate = this.startDate;
        }
        if (this.endDate !== undefined) {
            json.endDate = this.endDate;
        }
        return json;
    }

    setType(type: string): void {
        if (!Object.values(SubscriptionType).includes(type)) {
            throw new Error(`Invalid subscription type: ${type}`);
        }
        this._type = type;
    }

    set type(type: string) {
        this.setType(type);
    }

    get type(): string {
        return this._type;
    }

    isActive(): boolean {
        return !this.isExpired();
    }
}
